import { Injectable, Inject } from '@angular/core';
import { Instance } from '../interfaces/instance.interface';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { InstanceSelector } from '../interfaces/instanceSelector.interface';
import { Language } from '../interfaces/language.interface';
import { PortalMode } from '../enum/portalmode.enum';

@Injectable()
export class SessionService {
  constructor(
    protected http: HttpClient,
    protected router: Router,
    protected spinner: NgxSpinnerService,
    @Inject('API_URL') private apiUrl: string,
    @Inject('LOCAL_STORAGE_SESSION_ITEM_NAME') private itemName: string,
    @Inject('LANGUAGES') private languages: Language[]) {
  }

  private session: Session;

  public instances: Instance[];

  public activeInstanceId: string;
  public activeInstancePersonalNumber: string;
  public activeInstanceRole: string;
  public impersonated: string;

  public get numberOfInstances(): number {
    return this.instances ? this.instances.length : 0;
  };

  public get activeInstance(): Instance {
    const fallback: Instance = {
      id: this.activeInstanceId || '',
      name: '',
      roleType: this.activeInstanceRole || '',
      roleName: '',
      personalNumber: this.activeInstancePersonalNumber || '',
      defaultUrl: ''
    };

    if (!this.instances?.length) return fallback;

    return this.instances.find(i =>
      i.personalNumber === this.activeInstancePersonalNumber &&
      i.id === this.activeInstanceId &&
      i.roleType === this.activeInstanceRole
    ) || fallback;
  }

  public get hasActiveInstance(): boolean {
    return !!this.activeInstanceId && !!this.activeInstancePersonalNumber && !!this.activeInstanceRole;
  }

  public get hasSession(): boolean {
    return !!this.session;
  }

  public get hasToken(): boolean {
    return this.hasSession && !!this.session.accessToken;
  }

  public setSession(session: Session) {
    this.session = session;
    this.storeSession();
  }

  public storeSession() {
    if (!this.session) {
      localStorage.removeItem(this.itemName);
    } else {
      localStorage.setItem(this.itemName, JSON.stringify(this.session));
    }
  }

  public getSession(): Session {
    if (this.session) {
      return this.session;
    }

    const newSession = (): Session => {
      this.session = new Session();
      this.storeSession()
      return this.session;
    }

    const json: string = localStorage.getItem(this.itemName);
    if (!json || json === 'null' || json === 'undefined') {
      return newSession();
    }

    try {
      this.session = JSON.parse(json) as Session;
      if (this.languages.map(lang => lang.id).indexOf(this.session.language) === -1)
        this.session.language = this.languages[0].id;

    } catch (error) {
      console.error(error);
      return newSession();
    }

    return this.session;
  }

  public removeSession() {
    localStorage.removeItem(this.itemName);
    this.session = null;
  }

  public setInstance(instance: Instance, route?: string, impersonatedPersonalNumber?: string, impersonatedName?: string) {
    this.session = this.getSession();
    this.impersonated = impersonatedName;

    if (instance?.id && instance.roleType) {
      this.spinner.show();
      this.http.post(`${this.apiUrl}SetLoginType`, {
        loginType: instance.roleType,
        instanceId: instance.id,
        impersonatedPersonalNumber: impersonatedPersonalNumber
      }).toPromise()
        .then(() => {
          // Only log login for arbetstagare
          if (!impersonatedPersonalNumber) {
            this.http.post(`${this.apiUrl}LogLogin`, {
              instanceId: instance.id
            }).toPromise()
              .then(() => {
                this.setActiveInstanceOrNull(instance, route);
              });
          }
          else {
            this.setActiveInstanceOrNull(instance, route);
          }
        })
        .catch(error => {
          this.spinner.hide();
          console.error(error);
        });
    } else {
      this.setActiveInstance(instance ? instance.id : null, instance ? instance.personalNumber : null, instance ? instance.roleType : null, route);
    }
  }

  private setActiveInstanceOrNull(instance: Instance, route?: string) {
    this.spinner.hide();
    if (instance) {
      this.setActiveInstance(instance.id, instance.personalNumber, instance.roleType, route || instance.defaultUrl);
    } else {
      this.setActiveInstance(null, null, null, null);
    }
  }

  private setActiveInstance(id: string, personalNumber: string, role: string, route?: string) {
    this.getInstances().subscribe(
      (instanceSelector: InstanceSelector) => {
        this.activeInstanceId = id;
        this.activeInstancePersonalNumber = personalNumber;
        this.activeInstanceRole = role;

        if (!this.hasActiveInstance) {
          this.activeInstanceId = null;
          this.activeInstancePersonalNumber = null;
          this.activeInstanceRole = null;
          const portalMode = this.getPortalMode();

          if (portalMode === PortalMode.Borgenar) {
            if (!this.router.url.startsWith('/borgenar/start')) {
              this.router.navigate(['/borgenar/start'], { queryParams: { enforce: true } });
            }
          } else {
            if (!this.router.url.includes('mina-meddelanden')) {
              this.router.navigate(['/select-instance'], { queryParams: { enforce: true } });
            }
          }
          return;
        }

        this.storeSession();

        if (route) {
          this.router.navigate([route]);
        }
      },
      (error) => {
        this.instances = [];
      });
  }

  public setToken(token: string) {
    this.session = this.getSession();
    this.session.accessToken = token;
    this.storeSession();
  }

  public getToken(): string {
    this.session = this.getSession();
    if (!this.session) {
      return null;
    }

    return this.session.accessToken;
  }

  public logout() {
    this.activeInstanceId = null;
    this.activeInstancePersonalNumber = null;
    this.activeInstanceRole = null;

    this.session = this.getSession();
    this.session.portalMode = PortalMode.Default;
    this.storeSession();

    this.http.get(`${this.apiUrl}Logout`)
      .toPromise()
      .then(_ => {
        this.session.accessToken = null;
        this.storeSession();
      })
      .catch(error => {
        this.session.accessToken = null;
        this.storeSession();
        console.error(error);
      });
  }

  public getLanguage(): string {
    this.session = this.getSession();
    if (!this.session)
      return null;

    return this.session.language;
  }

  public setLanguage(language: string) {
    this.session = this.getSession();
    this.session.language = language;
    this.storeSession();
  }

  public getPersonalNumber(): string {
    this.session = this.getSession();
    if (!this.session)
      return null;

    return this.session.personalNumber;
  }

  public setPersonalNumber(personalNumber: string) {
    this.session = this.getSession();
    this.session.personalNumber = personalNumber;
    this.storeSession();
  }

  public getLoginMethod(): string {
    this.session = this.getSession();
    return this.session.loginMethod;
  }

  public setLoginMethod(loginMethod: string) {
    this.session = this.getSession();
    this.session.loginMethod = loginMethod;
    this.storeSession();
  }

  public getPortalMode(): PortalMode {
    if (window.location.hostname.includes('borgenarsportalen')) {
      return PortalMode.Borgenar;
    }
    this.session = this.getSession();
    if (this.hasToken) {
      return this.session.portalMode ?? PortalMode.Default;
    }
    return window.location.pathname.startsWith('/borgenar') ? PortalMode.Borgenar : PortalMode.Default;
  }

  public setPortalMode(mode: PortalMode): void {
    this.session = this.getSession();
    this.session.portalMode = mode;
    this.storeSession();
  }

  public selectInstance(callback?: () => void) {
    this.getInstances().subscribe(
      (instanceSelector: InstanceSelector) => {
        if (this.hasActiveInstance) {
          (callback || (() =>
            this.setActiveInstance(this.activeInstanceId, this.activeInstancePersonalNumber, this.activeInstanceRole)))();
          return;
        } else {
          this.activeInstanceId = null;
          this.activeInstancePersonalNumber = null;
          this.activeInstanceRole = null;
        }
        if (this.router.url.includes('mina-meddelanden'))
          callback();
        else
          this.router.navigate(['/select-instance'], { queryParams: { enforce: this.hasActiveInstance } });
      });
  }

  public getInstances(): Observable<InstanceSelector> {
    return this.http.get<InstanceSelector>(`${this.apiUrl}instances/get`).pipe(map(
      (instanceSelector) => {
        this.instances = instanceSelector.instances;
        return instanceSelector;
      }));
  }

  public getSelectedCaseId(): string {
    this.session = this.getSession();
    return this.session?.selectedCaseId;
  }

  public setSelectedCaseId(caseId: string) {
    this.session = this.getSession();
    this.session.selectedCaseId = caseId;
    this.storeSession();
  }

  public getHasCreditorClaims(): boolean {
    this.session = this.getSession();
    return this.session?.hasCreditorClaims ?? false;
  }

  public setHasCreditorClaims(hasClaims: boolean) {
    this.session = this.getSession();
    this.session.hasCreditorClaims = hasClaims;
    this.storeSession();
  }
}

export class Session {
  public accessToken: string;
  public language: string = 'se';
  public personalNumber: string;
  public loginMethod: string;
  public selectedCaseId?: string;
  public portalMode?: PortalMode;
  public hasCreditorClaims?: boolean;
}
